import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { FileSpreadsheet, Upload, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductsUploadSection = ({ onNavigateToImport }) => {
  const [uploading, setUploading] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const fileInputRef = useRef(null);
  const { token } = useAuth();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (!validExtensions.includes(fileExt)) {
      toast.error('Seuls les fichiers Excel (.xlsx, .xls) ou CSV sont acceptés');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/pharmacy/import`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 300000, // 5 minutes for large catalogs
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });
      
      setLastImport({
        success: true,
        imported: response.data.imported,
        updated: response.data.updated,
        total: response.data.total_processed,
        errors: response.data.errors || []
      });
      
      toast.success(`Import réussi: ${response.data.imported} nouveaux, ${response.data.updated} mis à jour`);
      
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      console.error('Import error:', error);
      const errorDetail = error.response?.data?.detail || error.message || 'Erreur lors de l\'import';
      setLastImport({
        success: false,
        error: errorDetail
      });
      toast.error(errorDetail);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-4 bg-gradient-to-r from-[#C45C26]/10 to-[#C45C26]/5 rounded-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#C45C26]/20 rounded-lg flex items-center justify-center">
          <FileSpreadsheet className="w-5 h-5 text-[#C45C26]" />
        </div>
        <div>
          <p className="font-medium text-[#5D4E37]">Import Catalogue Produits</p>
          <p className="text-sm text-[#8B7355]">
            Fichier Excel avec colonnes: CODE, DESIGNATION, FAMILLE, SOUS-FAMILLE, PV TTC, Reserve, Rayon, Robot
          </p>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleUpload}
          ref={fileInputRef}
          className="hidden"
          id="products-file-input"
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="bg-[#C45C26] hover:bg-[#A84B1F]"
          data-testid="upload-products-btn"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Import en cours...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Uploader Excel/CSV
            </>
          )}
        </Button>
        
        {onNavigateToImport && (
          <Button
            variant="outline"
            onClick={onNavigateToImport}
            className="text-[#C45C26] border-[#C45C26] hover:bg-[#C45C26]/10"
          >
            Voir les produits
          </Button>
        )}
      </div>
      
      {/* Import Result */}
      {lastImport && (
        <div className={`mt-4 p-3 rounded-lg ${lastImport.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-start gap-2">
            {lastImport.success ? (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
            )}
            <div>
              {lastImport.success ? (
                <>
                  <p className="font-medium text-green-800">Import réussi</p>
                  <p className="text-sm text-green-700">
                    {lastImport.imported} nouveaux produits ajoutés, {lastImport.updated} produits mis à jour
                  </p>
                  {lastImport.errors?.length > 0 && (
                    <p className="text-sm text-orange-600 mt-1">
                      {lastImport.errors.length} lignes ignorées (erreurs)
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="font-medium text-red-800">Erreur d'import</p>
                  <p className="text-sm text-red-700">{lastImport.error}</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Format Help */}
      <div className="mt-4 p-3 bg-white/50 rounded-lg text-xs text-[#8B7355]">
        <p className="font-medium mb-1">Colonnes attendues dans le fichier :</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li><strong>CODE</strong> - Code unique du produit</li>
          <li><strong>DESIGNATION</strong> ou <strong>NOM</strong> - Nom du produit</li>
          <li><strong>FAMILLE</strong> - Catégorie (COSMETIQUE, COMPLEMENT, etc.)</li>
          <li><strong>SOUS-FAMILLE</strong> - Sous-catégorie</li>
          <li><strong>PV TTC</strong> - Prix de vente TTC</li>
          <li><strong>Reserve</strong>, <strong>Rayon</strong>, <strong>Robot</strong> - Stocks</li>
        </ul>
      </div>
    </div>
  );
};

export default ProductsUploadSection;
